"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input, Button, Alert, Spinner } from "reactstrap"
import { StaffCarService } from "../../../../Service/StaffCarService"

interface OTPVerificationModalProps {
  isOpen: boolean
  token: string
  onVerificationSuccess: (tokenData: any) => void
  onClose: () => void
}

const OTPVerificationModal: React.FC<OTPVerificationModalProps> = ({
  isOpen,
  token,
  onVerificationSuccess,
  onClose,
}) => {
  const [otpCode, setOtpCode] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null)
  const [countdown, setCountdown] = useState(0)

  // Countdown timer for resend OTP
  useEffect(() => {
    let timer: NodeJS.Timeout
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000)
    }
    return () => clearTimeout(timer)
  }, [countdown])

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!otpCode.trim()) {
      setError("Please enter the OTP code")
      return
    }

    if (otpCode.length !== 6) {
      setError("OTP code must be 6 digits")
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await StaffCarService.verifyOTP(token, otpCode)
      
      // Check if verification was successful
      if (response.verified) {
        // Call success callback immediately
        onVerificationSuccess(response)
      } else {
        setError("OTP verification failed")
      }
    } catch (error: any) {
      const errorData = error.response?.data
      setError(errorData?.error || "Invalid OTP code")

      if (errorData?.remaining_attempts !== undefined) {
        setRemainingAttempts(errorData.remaining_attempts)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleResendOTP = async () => {
    if (countdown > 0) return

    setLoading(true)
    setError(null)
    setOtpCode("")

    try {
      await StaffCarService.generateOTP(token)
      setCountdown(60)
      setRemainingAttempts(null)
    } catch (error: any) {
      setError(error.response?.data?.error || "Failed to resend OTP")
    } finally {
      setLoading(false)
    }
  }

  const resetModal = () => {
    setOtpCode("")
    setError(null)
    setRemainingAttempts(null)
    setCountdown(0)
  }

  const handleClose = () => {
    resetModal()
    onClose()
  }

  return (
    <>
      <style>{`
        .otp-modal .modal-content {
          border: none;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          overflow: hidden;
        }
        
        .otp-modal .modal-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 24px 32px;
          text-align: center;
        }
        
        .otp-modal .modal-title {
          font-weight: 700;
          font-size: 1.4rem;
          margin: 0;
        }
        
        .otp-modal .modal-body {
          padding: 32px;
          background: #fafbfc;
        }
        
        .form-container {
          background: white;
          padding: 28px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }
        
        .form-group {
          margin-bottom: 24px;
        }
        
        .form-label {
          font-weight: 600;
          color: #374151;
          margin-bottom: 8px;
          font-size: 0.95rem;
        }
        
        .form-control {
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 14px 16px;
          font-size: 1rem;
          transition: all 0.2s ease;
          background: #fafbfc;
        }
        
        .form-control:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          background: white;
        }
        
        .otp-input {
          text-align: center;
          font-size: 1.5rem;
          font-weight: 600;
          letter-spacing: 0.5rem;
          font-family: 'Courier New', monospace;
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          border-radius: 12px;
          padding: 14px 28px;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.2s ease;
          width: 100%;
        }
        
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
        }
        
        .btn-outline-secondary {
          border: 2px solid #e5e7eb;
          color: #6b7280;
          border-radius: 12px;
          padding: 12px 24px;
          font-weight: 500;
          background: white;
          transition: all 0.2s ease;
        }
        
        .btn-outline-secondary:hover {
          border-color: #667eea;
          color: #667eea;
          background: #f8fafc;
        }
        
        .alert {
          border: none;
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 20px;
          font-weight: 500;
        }
        
        .alert-danger {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626;
        }
        
        .alert-success {
          background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
          color: #059669;
        }
        
        .resend-section {
          text-align: center;
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
        }
        
        .countdown-text {
          color: #6b7280;
          font-size: 0.9rem;
          margin-bottom: 12px;
        }
        
        .attempts-warning {
          background: #fef3c7;
          color: #d97706;
          padding: 12px;
          border-radius: 8px;
          font-size: 0.9rem;
          margin-bottom: 16px;
          text-align: center;
        }
      `}</style>

      <Modal isOpen={isOpen} toggle={handleClose} className="otp-modal" size="md" backdrop="static">
        <ModalHeader toggle={handleClose}>
          <div>
            <div className="modal-title">Security Verification</div>
            <div style={{ fontSize: "0.9rem", opacity: 0.9, marginTop: "4px" }}>
              Please verify your identity to continue
            </div>
          </div>
        </ModalHeader>

        <ModalBody>
          <div className="form-container">
            {error && (
              <Alert color="danger" className="alert alert-danger">
                {error}
              </Alert>
            )}

            {remainingAttempts !== null && remainingAttempts > 0 && (
              <div className="attempts-warning">
                ⚠️ {remainingAttempts} attempt{remainingAttempts !== 1 ? "s" : ""} remaining
              </div>
            )}

            <Form onSubmit={handleVerifyOTP}>
              <FormGroup>
                <Label className="form-label">Enter Verification Code</Label>
                <Input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="form-control otp-input"
                  disabled={loading}
                  maxLength={6}
                  required
                />
                <div style={{ fontSize: "0.85rem", color: "#6b7280", marginTop: "6px" }}>
                  Enter the 6-digit code sent to your company email
                </div>
              </FormGroup>

              <Button type="submit" className="btn-primary" disabled={loading || otpCode.length !== 6}>
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Verifying...
                  </>
                ) : (
                  "Verify & Continue"
                )}
              </Button>

              <div className="resend-section">
                {countdown > 0 ? (
                  <div className="countdown-text">Resend code in {countdown} seconds</div>
                ) : (
                  <div>
                    <div style={{ fontSize: "0.9rem", color: "#6b7280", marginBottom: "12px" }}>
                      Didn't receive the code?
                    </div>
                    <Button
                      type="button"
                      className="btn-outline-secondary"
                      onClick={handleResendOTP}
                      disabled={loading}
                      style={{ width: "auto", padding: "8px 20px" }}
                    >
                      Resend Code
                    </Button>
                  </div>
                )}
              </div>
            </Form>
          </div>
        </ModalBody>
      </Modal>
    </>
  )
}

export default OTPVerificationModal